<template>
  <div class="space-y-2">
    <UiEditableList
      :items="localConditions"
      itemKey="_id"
      :isRemovable="isEditable"
      @on-remove="onRemove">
      <template #item="{ item, index }">
        <div class="flex items-center gap-3">
          <UiInputSelectVee
            :name="`condition-operator-${asCondition(item)._id}`"
            :options="operatorOptions"
            class="mb-0!"
            v-model="asCondition(item).operator"
            @update:model-value="onConditionBlur" />
          <UiInputNumberFieldVee
            :name="`condition-value-${asCondition(item)._id}`"
            :min="0"
            :decimals="1"
            class="mb-0! w-28 shrink-0"
            v-model="asCondition(item).value"
            @blur="onConditionBlur" />
          <ColorPickerButton v-model="asCondition(item).colorID" />
          <!-- The extra slot holds the result-label combobox. It takes all the row's
               leftover width — the operator and value fields are pinned, since neither
               needs more than a few characters — so the combobox dropdown, which spans
               the trigger exactly (left: 0 / right: 0), stays wide enough for the
               "Opret ..." row to fit on one line. -->
          <div
            v-if="$slots.extra"
            class="min-w-0 flex-1">
            <slot
              name="extra"
              :condition="asCondition(item)"
              :index="index" />
          </div>
        </div>
      </template>
    </UiEditableList>

    <InfoText
      v-for="(error, i) in validationErrors"
      :key="i"
      :text="error"
      theme="warning" />

    <button
      v-if="isEditable"
      type="button"
      class="flex cursor-pointer items-center gap-1.5 text-sm text-action"
      @click="addCondition">
      <IconLucidePlus class="h-4 w-4" />
      <span>{{ $t('W: Tilføj betingelse') }}</span>
    </button>
  </div>
</template>

<script lang="ts">
import {
  computeValueBasedConditionErrors,
  sortValueBasedConditions,
} from '@/globals/typescript/utils/analysisTestFocusForm'
import type { TValueBasedConditionRow } from '@/globals/typescript/utils/analysisTestFocusForm'
import { generateFirestoreId } from '@/globals/typescript/utils/utils'
import type { PropType } from 'vue'

const OPERATOR_OPTIONS = [
  { value: '>=', text: '>=' },
  { value: '>', text: '>' },
  { value: '<=', text: '<=' },
  { value: '<', text: '<' },
]

export default {
  name: 'AnalysisTestConditionsList',

  emits: ['update:modelValue'],

  props: {
    modelValue: {
      type: Array as PropType<TValueBasedConditionRow[]>,
      required: true,
    },
    // Governs both the remove button on each row and the "Tilføj betingelse"
    // button below — callers gated by a permission (e.g. AnalysisTestEditColorsView)
    // pass their own canUser() result; callers with no gate (e.g. the create
    // wizard) leave this at the default.
    isEditable: {
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {
      localConditions: [...this.modelValue] as TValueBasedConditionRow[],
    }
  },

  computed: {
    operatorOptions() {
      return OPERATOR_OPTIONS
    },

    validationErrors(): string[] {
      return computeValueBasedConditionErrors(this.localConditions)
    },
  },

  watch: {
    modelValue(newVal: TValueBasedConditionRow[]) {
      this.localConditions = [...newVal]
    },
  },

  methods: {
    asCondition(item: unknown): TValueBasedConditionRow {
      return item as TValueBasedConditionRow
    },

    addCondition(): void {
      if (!this.isEditable) return
      this.localConditions = [
        ...this.localConditions,
        {
          _id: generateFirestoreId(),
          operator: '>=',
          value: 0,
          colorID: null,
          textID: '',
        },
      ]
      this.$emit('update:modelValue', this.localConditions)
    },

    onRemove(item: unknown): void {
      if (!this.isEditable) return
      const condition = this.asCondition(item)
      this.localConditions = this.localConditions.filter((c) => c._id !== condition._id)
      this.$emit('update:modelValue', this.localConditions)
    },

    onConditionBlur(): void {
      for (const condition of this.localConditions) {
        if (condition.value !== null && condition.value < 0) condition.value = 0
      }
      sortValueBasedConditions(this.localConditions)
      this.$emit('update:modelValue', this.localConditions)
    },
  },
}
</script>
